import { useEffect, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { BELT_ORDER, BELT_CLASS, beltLabel } from "@/lib/belts";
import { Download, Mail, UserCheck } from "lucide-react";

function daysSince(iso) {
  if (!iso) return Infinity;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return Infinity;
  return (Date.now() - then) / 86400000;
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function csvDownload(rows, fields, filename) {
  const header = fields.join(",");
  const body = rows.map(r => fields.map(f => `"${String(r[f] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function ActivitySection() {
  const [rows, setRows] = useState(null);
  const [trend, setTrend] = useState(null);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get("/analytics/activity").then(r => setRows(r.data.rows || []));
    api.get("/analytics/activity/trend", { params: { days: 14 } }).then(r => setTrend(r.data));
  }, []);

  const emailExport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/analytics/activity/email");
      alert(`Sent — ${data.rows} rows emailed to your inbox.`);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not send email");
    }
    setEmailing(false);
  };

  const maxTrend = trend ? Math.max(...trend.values, 1) : 1;
  const isInactive = (u) => u.role === "student" && daysSince(u.last_login_at) >= 14;
  const inactiveCount = (rows || []).filter(isInactive).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="activity-section">
      <div className="card p-6">
        <div className="flex items-center justify-between gap-2 mb-5 flex-wrap">
          <div className="label">Daily active · last 14 days</div>
        </div>
        {!trend && <div className="label-tiny">Loading…</div>}
        {trend && (
          <>
            <div className="flex items-end gap-1.5 h-32">
              {trend.values.map((v, i, a) => (
                <div key={i} className="bar flex-1 rounded-t-md" style={{ height: `${(v / maxTrend) * 100}%`, minHeight: v > 0 ? 4 : 1, background: i === a.length - 1 ? "var(--green)" : "var(--border-strong)" }} title={`${trend.labels[i]}: ${v}`} />
              ))}
            </div>
            <div className="flex justify-between mt-2">
              <span className="label-tiny">{trend.labels[0]?.slice(5)}</span>
              <span className="label-tiny">{trend.labels[trend.labels.length - 1]?.slice(5)}</span>
            </div>
          </>
        )}
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <UserCheck size={14} style={{color:"var(--green)"}}/>
            <div className="label">Team & students · by last active</div>
            {inactiveCount > 0 && (
              <span className="label-tiny" style={{color:"var(--danger)", border:"1px solid var(--danger)", borderRadius:999, padding:"2px 8px"}} data-testid="inactive-alert-badge">
                {inactiveCount} inactive 14d+
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => csvDownload(rows || [], ["name","email","role","last_login_at","created_at"], "activity.csv")}
              className="btn btn-outline" style={{padding:"5px 8px", fontSize: 11}} data-testid="activity-download-csv">
              <Download size={11}/> CSV
            </button>
            <button onClick={emailExport} disabled={emailing} className="btn btn-outline" style={{padding:"5px 8px", fontSize: 11}} data-testid="activity-email-csv">
              <Mail size={11}/> {emailing ? "Sending…" : "Email"}
            </button>
          </div>
        </div>
        <div style={{maxHeight: 260, overflowY: "auto"}}>
          {rows && rows.length === 0 && <div className="label-tiny py-4 text-center" style={{color:"var(--text-dim)"}}>No team or students yet.</div>}
          {(rows || []).map(u => (
            <div key={u.user_id} className="flex items-center justify-between gap-3 py-2 border-t" style={{borderColor:"var(--border)"}} data-testid={`activity-row-${u.user_id}`}>
              <div className="min-w-0 flex items-center gap-2">
                <div>
                  <div className="font-semibold text-[13px] truncate">{u.name || u.email}</div>
                  <div className="label-tiny truncate" style={{color:"var(--text-dim)"}}>{u.email} · {u.role}</div>
                </div>
                {isInactive(u) && (
                  <span className="label-tiny shrink-0" style={{color:"var(--danger)", border:"1px solid var(--danger)", borderRadius:999, padding:"1px 7px"}} data-testid={`inactive-tag-${u.user_id}`}>
                    Inactive
                  </span>
                )}
              </div>
              <div className="label-tiny shrink-0 mono" style={isInactive(u) ? {color:"var(--danger)"} : undefined}>{timeAgo(u.last_login_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-8 label">Loading…</div>;
  const max12 = Math.max(...data.attendance_12w, 1);
  const totalBelts = Object.values(data.belt_distribution).reduce((a,b)=>a+b,0) || 1;
  // Only show belts with at least one student, so the chart doesn't drown in empty rows
  // for academies that don't run a kids program (and vice-versa).
  const beltsToShow = BELT_ORDER.filter(b => (data.belt_distribution[b] || 0) > 0);
  // If truly empty, fall back to adult belts so the section is still meaningful.
  const rows = beltsToShow.length ? beltsToShow : ["white", "blue", "purple", "brown", "black"];

  return (
    <div data-testid="page-analytics">
      <PageHeader section="Roster" title="Analytics"/>
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card kpi-card"><div className="label">Active members</div><div className="kpi-value mt-3" style={{color:"var(--green)"}}>{data.active_members}</div><div className="label-tiny mt-2">{data.active_delta ? `↑ ${data.active_delta} this month` : "No new signups yet"}</div></div>
          <div className="card kpi-card"><div className="label">Avg attendance</div><div className="kpi-value mt-3">{data.avg_attendance_per_week ? `${data.avg_attendance_per_week}x` : "—"}</div><div className="label-tiny mt-2">Per week</div></div>
          <div className="card kpi-card"><div className="label">Inactive · 14d</div><div className="kpi-value mt-3">{data.inactive_14d}</div><div className="label-tiny mt-2">Needs follow-up</div></div>
          <div className="card kpi-card"><div className="label">Trial → Paid</div><div className="kpi-value mt-3">{data.trial_to_paid == null ? "—" : `${data.trial_to_paid}%`}</div><div className="label-tiny mt-2">Last 30 days</div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-6">
            <div className="label mb-5">Attendance trend · 12 weeks</div>
            <div className="flex items-end gap-2 h-44">
              {data.attendance_12w.map((v,i,a) => (
                <div key={i} className="bar flex-1 rounded-t-md" style={{ height: `${(v/max12)*100}%`, background: i===a.length-1 ? "var(--green)" : "var(--border-strong)" }}/>
              ))}
            </div>
            <div className="flex justify-between mt-2"><span className="label-tiny">Aug</span><span className="label-tiny">Sep</span><span className="label-tiny">Oct</span></div>
          </div>
          <div className="card p-6">
            <div className="label mb-5">Inactive students</div>
            <div className="space-y-3">
              {data.inactive_students.map(s => (
                <div key={s.student_id} className="flex items-center justify-between" data-testid={`inactive-${s.student_id}`}>
                  <div>
                    <div className="font-semibold text-[14px] uppercase tracking-tight">{s.name}</div>
                    <div className="label-tiny mt-0.5" style={{color:"var(--danger)"}}>No activity · {s.last_seen}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="label-tiny">${s.membership_price || 120}/mo</span>
                    <button className="btn btn-outline" style={{padding:"6px 14px", fontSize:11}}>Reach out</button>
                  </div>
                </div>
              ))}
              {data.inactive_students.length === 0 && <div className="label-tiny">No inactive students.</div>}
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="label mb-5">Belt distribution</div>
          <div className="space-y-3" data-testid="belt-distribution">
            {rows.map(b => {
              const count = data.belt_distribution[b] || 0;
              const pct = (count/totalBelts)*100;
              return (
                <div key={b} className="flex items-center gap-4" data-testid={`belt-row-${b}`}>
                  <div className="label-tiny" style={{width:96}}>{beltLabel(b)}</div>
                  <div className="flex-1 h-3 rounded-full overflow-hidden" style={{background:"var(--bg-elev)"}}>
                    <div className={`h-full rounded-full ${BELT_CLASS[b] || "belt-white"}`} style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="mono text-sm" style={{width:30, textAlign:"right"}}>{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        <ActivitySection />
      </div>
    </div>
  );
}
